import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { TooltipSystem } from "@/components/tooltips/TooltipSystem";
import { useSentryContext } from "@/hooks/useSentryContext";
import DashboardLayout from "@/components/DashboardLayout";
// import ChatAI from "@/components/ChatAI"; // Masqué temporairement - pas encore configuré
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Team from "./pages/Team";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Reports from "./pages/Reports";
import Subscription from "./pages/Subscription";
import AuditHistory from "./pages/AuditHistory";
import NotFound from "./pages/NotFound";
import FinancialManagement from "./pages/FinancialManagement";
import Rentabilite from "./pages/Rentabilite";
import Tresorerie from "./pages/Tresorerie";
import Pricing from "./pages/Pricing";
import Onboarding from "./pages/Onboarding";
import Settings from "./pages/Settings";
import PaymentSetup from "./pages/PaymentSetup";
import PaymentBlocked from "./pages/PaymentBlocked";
import ResetPassword from "./pages/ResetPassword";

// Pages Product (publiques)
import Product from "./pages/Product";
import RentabiliteFeature from "./pages/product/Rentabilite";
import JourCritiqueFeature from "./pages/product/JourCritique";
import RetardsFeature from "./pages/product/Retards";
import PlanningFeature from "./pages/product/Planning";
import EquipesFeature from "./pages/product/Equipes";
import AnalyseIAFeature from "./pages/product/AnalyseIA";
import CentrePilotageFeature from "./pages/product/CentrePilotage";
import RapportsFeature from "./pages/product/Rapports";

// Pages Use Cases (publiques)
import UseCases from "./pages/UseCases";

// Pages Blog (publiques)
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";

// Pages Légales (publiques)
import CGU from "./pages/CGU";
import MentionsLegales from "./pages/MentionsLegales";
import Confidentialite from "./pages/Confidentialite";
import CGV from "./pages/CGV";
import Contact from "./pages/Contact";
import QuiSommesNous from "./pages/QuiSommesNous";
import Electricien from "./pages/use-cases/Electricien";
import Plombier from "./pages/use-cases/Plombier";
import Macon from "./pages/use-cases/Macon";
import Peintre from "./pages/use-cases/Peintre";
import Couvreur from "./pages/use-cases/Couvreur";
import MultiCorps from "./pages/use-cases/MultiCorps";

import ProfileSettings from "./pages/settings/ProfileSettings";
import EntrepriseSettings from "./pages/settings/EntrepriseSettings";
import SubscriptionSettings from "./pages/settings/SubscriptionSettings";
import AlertesSettings from "./pages/settings/AlertesSettings";

// Admin Panel
import { CookieBanner } from "@/components/CookieBanner";
import { SuperAdminRoute } from "@/components/auth/SuperAdminRoute";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminEntreprises from "./pages/admin/AdminEntreprises";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Désactiver le refetch automatique quand la fenêtre reprend le focus
      // Cela évite que les modals se ferment ou s'actualisent quand l'utilisateur
      // change d'onglet ou de fenêtre
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,       // Pas de refetch au retour d'onglet (WebSocket reconnect)
      staleTime: 5 * 60 * 1000,      // 5 minutes — données "fraîches" pendant 5 min
      gcTime: 10 * 60 * 1000,         // 10 minutes — garbage collection du cache
      retry: 1,                        // 1 retry en cas d'erreur (défaut RQ = 3)
    },
  },
});

// Composant interne qui utilise les hooks nécessitant les providers
const AppContent = () => {
  // Enrichir automatiquement Sentry avec le contexte métier
  useSentryContext();

  return (
    <>
      <Toaster />
      <Sonner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<Pricing />} />

              {/* Pages Product (publiques) */}
              <Route path="/product" element={<Product />} />
              <Route path="/product/rentabilite" element={<RentabiliteFeature />} />
              <Route path="/product/jour-critique" element={<JourCritiqueFeature />} />
              <Route path="/product/retards" element={<RetardsFeature />} />
              <Route path="/product/planning" element={<PlanningFeature />} />
              <Route path="/product/equipes" element={<EquipesFeature />} />
              <Route path="/product/analyse-ia" element={<AnalyseIAFeature />} />
              <Route path="/product/centre-pilotage" element={<CentrePilotageFeature />} />
              <Route path="/product/rapports" element={<RapportsFeature />} />

              {/* Pages Use Cases (publiques) */}
              <Route path="/use-cases" element={<UseCases />} />
              <Route path="/use-cases/electricien" element={<Electricien />} />
              <Route path="/use-cases/plombier" element={<Plombier />} />
              <Route path="/use-cases/macon" element={<Macon />} />
              <Route path="/use-cases/peintre" element={<Peintre />} />
              <Route path="/use-cases/couvreur" element={<Couvreur />} />
              <Route path="/use-cases/multi-corps" element={<MultiCorps />} />

              {/* Pages Blog (publiques) */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />

              {/* Pages Légales (publiques) */}
              <Route path="/cgu" element={<CGU />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />

              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/payment-setup" element={<PaymentSetup />} />
              <Route path="/payment-blocked" element={<PaymentBlocked />} />
              <Route
                element={
                  <ProtectedRoute>
                    <TooltipSystem>
                      <DashboardLayout />
                    </TooltipSystem>
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
                <Route path="/team" element={<Team />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/rentabilite" element={<Rentabilite />} />
                <Route path="/financial" element={<FinancialManagement />} />
                <Route path="/financial/:chantierId" element={<FinancialManagement />} />
                <Route path="/tresorerie" element={<Tresorerie />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/audit" element={<AuditHistory />} />

                {/* Page Réglages avec sous-routes */}
                <Route path="/settings" element={<Settings />}>
                  <Route index element={<Navigate to="/settings/profile" replace />} />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="entreprise" element={<EntrepriseSettings />} />
                  <Route path="alertes" element={<AlertesSettings />} />
                  <Route path="subscription" element={<SubscriptionSettings />} />
                </Route>
              </Route>

              {/* Admin Panel - super_admin uniquement (hors ProtectedRoute, pas de vérif Stripe) */}
              <Route
                path="/admin"
                element={
                  <SuperAdminRoute>
                    <AdminLayout />
                  </SuperAdminRoute>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="entreprises" element={<AdminEntreprises />} />
                <Route path="subscriptions" element={<AdminSubscriptions />} />
              </Route>

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* <ChatAI /> */}{/* Masqué temporairement - pas encore configuré */}
      <CookieBanner />
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <TooltipProvider>
          <SubscriptionProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </SubscriptionProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
